import React, { useState, useCallback, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Divider, Grid, Typography } from '@mui/material'
import { useParams } from '@reach/router'
import { useDispatch } from 'react-redux'
import { Loader } from '@refera/ui-web'

import * as Modal from '_components/modal/generic-modal'
import * as Input from '_/components/inputs/Input'
import { BasicInput, MaskBasicInput } from '_/components/inputs'
import { ControlledCpfCnpjInput } from '_/components/inputs/MaskInput/common/CpfCnpjInput'
import { postBuildingUnit, patchBuildingUnit } from '_/modules/agency/actions'

import { EMAIL_REGEX, PHONE_REGEX, CPF_CNPJ_REGEX } from '_/utils/constants'
import { useDialog } from '_/hooks/use-dialog'
import { useToast } from '_/hooks/use-toast'
import useStyles from './styles'

const UnitModal = ({ unitData, isOpen, handleClose, onSuccess }) => {
  const styles = useStyles()
  const formRef = useRef()
  const { showDialog, closeDialog } = useDialog()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const [cpfCnpjToValidate, setCpfCnpjToValidate] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { agencyId, id } = useParams()

  const methods = useForm({
    mode: 'all',
  })
  const {
    errors,
    watch,
    formState: { dirtyFields, isValid },
  } = methods

  const handleCpfCnpjBlur = useCallback(() => {
    if (errors?.cpfCnpj) return

    const value = watch('cpfCnpj')
    const onlyNumbers = value.replace(/\D/g, '') || ''

    if (onlyNumbers === unitData?.cpfCnpj || value === cpfCnpjToValidate) return

    setCpfCnpjToValidate(value)
  }, [errors?.cpfcnpf, cpfCnpjToValidate, unitData?.cpfCnpj])

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          handleClose()
        },
        onCancel: closeDialog,
      })
    } else {
      handleClose()
    }
  }

  const onSubmit = useCallback(
    data => {
      if (!isValid) return

      const transformedData = {
        ...data,
        building: id,
        area: data?.area ? Number(data.area.replace(',', '.')) : '',
      }

      setIsLoading(true)
      const action = unitData?.id
        ? patchBuildingUnit(agencyId, id, unitData?.id, transformedData)
        : postBuildingUnit(agencyId, id, transformedData)

      dispatch(action)
        .then(() => {
          showToast({
            type: 'success',
          })
          onSuccess()
          handleClose()
        })
        .catch(error => {
          const message = error?.error_message || null
          showToast({
            type: 'error',
            message,
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [isValid, agencyId, id, unitData?.id]
  )

  return (
    <>
      <Loader open={isLoading} hasBackdrop />
      <form ref={formRef} className={styles.modal} id="unit-data">
        <FormProvider {...methods}>
          <Modal.Root open={isOpen} onClose={handleClose} disableEnforceFocus>
            <Modal.TitleModal>
              <Typography className={styles.modalTitle}>Dados da unidade</Typography>
            </Modal.TitleModal>
            <Divider className={styles.divider} />
            <Modal.Content className={styles.form}>
              <Grid className={styles.reference1}>
                <BasicInput
                  name="reference_1"
                  label="Referência 1"
                  labelClasses={styles.label}
                  defaultValue={unitData?.reference1 || ''}
                  rules={{
                    maxLength: {
                      value: 50,
                      message: `Esse campo aceita no máximo 50 caracteres`,
                    },
                  }}
                />
              </Grid>
              <Grid className={styles.reference2}>
                <BasicInput
                  name="reference_2"
                  label="Referência 2"
                  labelClasses={styles.label}
                  defaultValue={unitData?.reference2 || ''}
                  rules={{
                    maxLength: {
                      value: 50,
                      message: `50 caracteres`,
                    },
                  }}
                />
              </Grid>
              <Grid className={styles.area}>
                <Input.Root name="area" defaultValue={unitData?.area}>
                  <Input.Label labelClasses={styles.label} name="area">
                    Área
                  </Input.Label>
                  <Input.ControllerNumber name="area" decimalScale={2} />
                </Input.Root>
              </Grid>
              <Grid className={styles.owner}>
                <BasicInput
                  name="owner"
                  label="Proprietário"
                  labelClasses={styles.label}
                  defaultValue={unitData?.owner || ''}
                  rules={{
                    maxLength: {
                      value: 250,
                      message: `Esse campo aceita no máximo 250 caracteres`,
                    },
                  }}
                />
              </Grid>
              <Grid className={styles.cpfCnpj}>
                <ControlledCpfCnpjInput
                  name="cpfCnpj"
                  label="CPF/CNPJ"
                  showErrorMessage
                  errorMessage={errors?.cpfCnpj?.message}
                  labelProps={{ className: styles.label }}
                  className={styles.cpfCnpjField}
                  controllerProps={{
                    rules: {
                      pattern: {
                        value: CPF_CNPJ_REGEX,
                        message: 'CPF/CNPJ inválido',
                      },
                    },
                  }}
                  fieldProps={{
                    error: !!errors?.cpfCnpj,
                    onBlur: handleCpfCnpjBlur,
                  }}
                />
              </Grid>
              <Grid className={styles.phone}>
                <MaskBasicInput
                  name="phone"
                  label="Fone/Celular"
                  mask="+55 (99) 9 9999-9999"
                  labelClasses={styles.label}
                  rules={{
                    pattern: { value: PHONE_REGEX, message: 'Telefone inválido' },
                  }}
                  defaultValue={unitData?.phone || ''}
                />
              </Grid>
              <Grid className={styles.emails}>
                <BasicInput
                  name="emails"
                  label="Emails"
                  labelClasses={styles.label}
                  defaultValue={unitData?.emails}
                  rules={{
                    pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
                  }}
                />
              </Grid>
            </Modal.Content>
            <Modal.Actions className={styles.actionsModal}>
              <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
              <Modal.ButtonFullBlue
                form="requester-data"
                onClick={methods.handleSubmit(onSubmit)}
                disabled={isLoading || !isValid}
              >
                Salvar
              </Modal.ButtonFullBlue>
            </Modal.Actions>
          </Modal.Root>
        </FormProvider>
      </form>
    </>
  )
}

export default UnitModal
